import { DatePipe, DecimalPipe, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { HighlightTextPipe } from '@nk/ui-lib';
import * as dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import { ParsedRiskContext } from '../../utils/screening-case-review-context.utils';

dayjs.extend(customParseFormat);

type Field = { tag?: string | null; name?: string | null; value?: string | null };

@Component({
  selector: 'nk-screening-case-supplier-risk-context',
  imports: [DatePipe, DecimalPipe, LowerCasePipe, TranslateModule, HighlightTextPipe],
  templateUrl: './screening-case-supplier-risk-context.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningCaseSupplierRiskContextComponent {
  public risk = input.required<ParsedRiskContext>();
  public showHeader = input(true);
  public highlightTexts = input<string[]>([]);

  public getAmlTypeBadgeClass(amlType: string): string {
    return amlType?.toLowerCase().includes('sanction') ? 'bg-danger text-white' : 'bg-warning text-black';
  }

  public isPassportField(field: Field | null | undefined): boolean {
    return field?.tag === 'passport' || field?.name === 'Passport';
  }

  public isAdditionalSanctionsInfoField(field: Field | null | undefined): boolean {
    return field?.name === 'Additional Sanctions Information';
  }

  public getRegularFields(fields: Field[] | null | undefined): Field[] {
    if (!fields || !Array.isArray(fields)) {
      return [];
    }
    return fields.filter(
      (field) =>
        (field?.tag === 'date_of_birth' ||
          field?.name === 'Date of Birth' ||
          field?.name === 'Designation Date' ||
          field?.name === 'Legal Basis' ||
          field?.name === 'Sanction Type' ||
          field?.name === 'Reason' ||
          field?.name === 'Other Info') &&
        field?.tag !== 'passport' &&
        field?.name !== 'Passport' &&
        field?.name !== 'Additional Sanctions Information',
    );
  }

  public getPassportFields(fields: Field[] | null | undefined): Field[] {
    if (!fields || !Array.isArray(fields)) {
      return [];
    }
    return fields.filter((field) => this.isPassportField(field));
  }

  public getAdditionalSanctionsInfoFields(fields: Field[] | null | undefined): Field[] {
    if (!fields || !Array.isArray(fields)) {
      return [];
    }
    return fields.filter((field) => this.isAdditionalSanctionsInfoField(field));
  }

  public isDateField(field: Field | null | undefined): boolean {
    return field?.tag === 'date_of_birth' || field?.name === 'Date of Birth' || field?.name === 'Designation Date';
  }

  public parseDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    // Try parsing DD-MM-YYYY format first
    const parsed = dayjs(value, 'DD-MM-YYYY', true);
    if (parsed.isValid()) {
      return parsed.toDate();
    }
    // Fallback to default parsing (ISO format, etc.)
    const fallback = dayjs(value);
    return fallback.isValid() ? fallback.toDate() : null;
  }
}
